// GroupDetail Component - View a single group

import React, { useState, useEffect } from 'react';
import {
  ArrowLeft,
  Users,
  Calendar,
  MessageSquare,
  Settings,
  MoreHorizontal,
  Lock,
  Globe,
  MapPin,
  UserPlus,
  LogOut,
  Shield,
  Clock,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import {
  getGroupWithMembership,
  joinGroup,
  leaveGroup,
  subscribeToGroup,
  subscribeToGroupPosts,
  subscribeToGroupEvents,
  getGroupMembers,
} from '../../services/groupsService';
import type {
  GroupWithMembership,
  GroupPost,
  GroupEvent,
  GroupMember,
} from '../../types/groups';
import { GroupFeed } from './GroupFeed';
import { GroupMembers } from './GroupMembers';
import { GroupEvents } from './GroupEvents';
import { GroupSettings } from './GroupSettings';
import { Timestamp } from '@firebase/firestore';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';

interface GroupDetailProps {
  groupId: string;
  onBack: () => void;
  onViewPlace?: (placeId: string) => void;
  onViewProfile?: (userId: string) => void;
}

type Tab = 'feed' | 'events' | 'members';

// Get category info
const getCategoryInfo = (category: string) => {
  const categories: Record<string, { label: string; color: string }> = {
    social: { label: 'Gesellig', color: 'bg-pink-100 text-pink-700' },
    hobby: { label: 'Hobby', color: 'bg-purple-100 text-purple-700' },
    neighborhood: { label: 'Nachbarschaft', color: 'bg-green-100 text-green-700' },
    food_drink: { label: 'Essen & Trinken', color: 'bg-orange-100 text-orange-700' },
    culture: { label: 'Kultur', color: 'bg-blue-100 text-blue-700' },
    learning: { label: 'Lernen', color: 'bg-yellow-100 text-yellow-700' },
    sports: { label: 'Sport', color: 'bg-red-100 text-red-700' },
    nature: { label: 'Natur', color: 'bg-emerald-100 text-emerald-700' },
    other: { label: 'Sonstiges', color: 'bg-gray-100 text-gray-700' },
  };
  return categories[category] || categories.other;
};

export function GroupDetail({
  groupId,
  onBack,
  onViewPlace,
  onViewProfile,
}: GroupDetailProps) {
  const { user } = useAuth();
  const [group, setGroup] = useState<GroupWithMembership | null>(null);
  const [posts, setPosts] = useState<GroupPost[]>([]);
  const [events, setEvents] = useState<GroupEvent[]>([]);
  const [members, setMembers] = useState<GroupMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<Tab>('feed');
  const [joining, setJoining] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  // Load group
  useEffect(() => {
    const loadGroup = async () => {
      setLoading(true);
      const groupData = await getGroupWithMembership(groupId, user?.uid || null);
      setGroup(groupData);
      setLoading(false);
    };
    loadGroup();
  }, [groupId, user?.uid]);

  // Subscribe to real-time updates
  useEffect(() => {
    const unsubGroup = subscribeToGroup(groupId, (updatedGroup) => {
      if (updatedGroup) {
        setGroup((prev) =>
          prev
            ? { ...prev, ...updatedGroup }
            : null
        );
      }
    });

    const unsubPosts = subscribeToGroupPosts(groupId, setPosts);
    const unsubEvents = subscribeToGroupEvents(groupId, setEvents);

    return () => {
      unsubGroup();
      unsubPosts();
      unsubEvents();
    };
  }, [groupId]);

  // Load members when tab changes
  useEffect(() => {
    if (activeTab === 'members' && members.length === 0) {
      getGroupMembers(groupId).then(setMembers);
    }
  }, [activeTab, groupId, members.length]);

  const handleJoin = async () => {
    if (!user || !group) return;
    setJoining(true);
    try {
      const result = await joinGroup(groupId, user.uid, user.displayName || 'Anonym', user.photoURL || undefined);
      if (result.pending) {
        setGroup((prev) => prev ? { ...prev, isPending: true } : null);
      } else {
        // Reload group data
        const updatedGroup = await getGroupWithMembership(groupId, user.uid);
        setGroup(updatedGroup);
      }
    } catch (err: any) {
      alert(err.message || 'Fehler beim Beitreten');
    } finally {
      setJoining(false);
    }
  };

  const handleLeave = async () => {
    if (!user || !group) return;
    if (!confirm('Moechtest du die Gruppe wirklich verlassen?')) return;

    try {
      await leaveGroup(groupId, user.uid);
      const updatedGroup = await getGroupWithMembership(groupId, user.uid);
      setGroup(updatedGroup);
    } catch (err: any) {
      alert(err.message || 'Fehler beim Verlassen');
    }
  };

  const formatDate = (date: Timestamp | Date | undefined) => {
    if (!date) return '';
    const d = date instanceof Timestamp ? date.toDate() : date;
    return format(d, 'd. MMMM yyyy', { locale: de });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!group) {
    return (
      <div className="min-h-screen bg-gray-50">
        <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
          <div className="max-w-3xl mx-auto px-4 py-4">
            <button onClick={onBack} className="p-2 hover:bg-gray-100 rounded-full">
              <ArrowLeft className="w-5 h-5" />
            </button>
          </div>
        </header>
        <div className="max-w-3xl mx-auto px-4 py-12 text-center">
          <Users className="w-16 h-16 mx-auto text-gray-300 mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Gruppe nicht gefunden</h2>
          <button onClick={onBack} className="px-4 py-2 bg-indigo-500 text-white rounded-lg">
            Zurueck
          </button>
        </div>
      </div>
    );
  }

  const categoryInfo = getCategoryInfo(group.category);
  const isAdmin = group.currentUserRole === 'creator' || group.currentUserRole === 'admin';

  // Show Settings Panel
  if (showSettings) {
    return (
      <GroupSettings
        groupId={groupId}
        onBack={() => setShowSettings(false)}
        onGroupUpdated={async () => {
          const updatedGroup = await getGroupWithMembership(groupId, user?.uid || null);
          setGroup(updatedGroup);
        }}
        onGroupDeleted={() => {
          // Navigate back to the groups list after deletion
          onBack();
        }}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Cover Image */}
      <div className="relative h-48 bg-gradient-to-br from-indigo-400 to-purple-500">
        {group.coverImageUrl ? (
          <img
            src={group.coverImageUrl}
            alt={group.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Users className="w-20 h-20 text-white/30" />
          </div>
        )}

        {/* Back button overlay */}
        <div className="absolute top-0 left-0 right-0 p-4 flex items-center justify-between">
          <button
            onClick={onBack}
            className="p-2 bg-black/30 hover:bg-black/50 text-white rounded-full backdrop-blur-sm transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>

          <div className="relative">
            <button
              onClick={() => setShowMenu(!showMenu)}
              className="p-2 bg-black/30 hover:bg-black/50 text-white rounded-full backdrop-blur-sm transition-colors"
            >
              <MoreHorizontal className="w-5 h-5" />
            </button>

            {showMenu && (
              <div className="absolute right-0 top-12 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                {isAdmin && (
                  <button
                    onClick={() => {
                      setShowMenu(false);
                      setShowSettings(true);
                    }}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                  >
                    <Settings className="w-4 h-4" />
                    Einstellungen
                  </button>
                )}
                {group.isMember && (
                  <button
                    onClick={() => {
                      setShowMenu(false);
                      handleLeave();
                    }}
                    className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                  >
                    <LogOut className="w-4 h-4" />
                    Gruppe verlassen
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Group Info */}
      <div className="max-w-3xl mx-auto px-4">
        <div className="bg-white rounded-xl border border-gray-200 -mt-8 relative z-10 p-6">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <span className={`px-2 py-0.5 text-xs rounded-full ${categoryInfo.color}`}>
                  {categoryInfo.label}
                </span>
                {group.visibility === 'private' ? (
                  <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                    <Lock className="w-3 h-3" /> Privat
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                    <Globe className="w-3 h-3" /> Oeffentlich
                  </span>
                )}
              </div>

              <h1 className="text-2xl font-bold text-gray-900">{group.name}</h1>

              <p className="text-gray-600 mt-2">{group.description}</p>

              {/* Stats */}
              <div className="flex items-center gap-4 mt-4 text-sm text-gray-500">
                <span className="flex items-center gap-1">
                  <Users className="w-4 h-4" />
                  {group.memberCount} Mitglieder
                </span>
                <span className="flex items-center gap-1">
                  <MessageSquare className="w-4 h-4" />
                  {group.postCount} Beitraege
                </span>
                <span className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  {group.eventCount} Events
                </span>
              </div>

              {/* Linked Places */}
              {group.linkedPlaces.length > 0 && (
                <div className="mt-4">
                  <div className="flex flex-wrap gap-2">
                    {group.linkedPlaces.slice(0, 3).map((place) => (
                      <button
                        key={place.placeId}
                        onClick={() => onViewPlace?.(place.placeId)}
                        className="inline-flex items-center gap-1 px-3 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm rounded-full transition-colors"
                      >
                        <MapPin className="w-3 h-3" />
                        {place.placeName}
                      </button>
                    ))}
                    {group.linkedPlaces.length > 3 && (
                      <span className="text-sm text-gray-500">
                        +{group.linkedPlaces.length - 3} weitere
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Join Button */}
            <div className="flex-shrink-0">
              {user ? (
                group.isMember ? (
                  <span className="inline-flex items-center gap-1 px-4 py-2 bg-green-100 text-green-700 rounded-lg text-sm font-medium">
                    <Shield className="w-4 h-4" />
                    Mitglied
                  </span>
                ) : group.isPending ? (
                  <span className="inline-flex items-center gap-1 px-4 py-2 bg-yellow-100 text-yellow-700 rounded-lg text-sm font-medium">
                    <Clock className="w-4 h-4" />
                    Angefragt
                  </span>
                ) : (
                  <button
                    onClick={handleJoin}
                    disabled={joining}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 disabled:opacity-50 transition-colors"
                  >
                    {joining ? (
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <UserPlus className="w-4 h-4" />
                    )}
                    {group.joinPolicy === 'request' ? 'Beitritt anfragen' : 'Beitreten'}
                  </button>
                )
              ) : null}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mt-4 bg-white rounded-lg border border-gray-200 p-1">
          <button
            onClick={() => setActiveTab('feed')}
            className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-colors ${
              activeTab === 'feed'
                ? 'bg-indigo-500 text-white'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <MessageSquare className="w-4 h-4 inline mr-2" />
            Beitraege
          </button>
          <button
            onClick={() => setActiveTab('events')}
            className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-colors ${
              activeTab === 'events'
                ? 'bg-indigo-500 text-white'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <Calendar className="w-4 h-4 inline mr-2" />
            Events
            {events.length > 0 && (
              <span className="ml-1 px-1.5 py-0.5 text-xs bg-white/20 rounded-full">
                {events.length}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('members')}
            className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-colors ${
              activeTab === 'members'
                ? 'bg-indigo-500 text-white'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <Users className="w-4 h-4 inline mr-2" />
            Mitglieder
          </button>
        </div>

        {/* Tab Content */}
        <div className="py-4 pb-20">
          {activeTab === 'feed' && (
            <GroupFeed
              groupId={groupId}
              posts={posts}
              isMember={group.isMember}
              onViewProfile={onViewProfile}
              onViewPlace={onViewPlace}
            />
          )}

          {activeTab === 'events' && (
            <GroupEvents
              groupId={groupId}
              events={events}
              isMember={group.isMember}
              onViewPlace={onViewPlace}
            />
          )}

          {activeTab === 'members' && (
            <GroupMembers
              groupId={groupId}
              members={members}
              currentUserRole={group.currentUserRole}
              onViewProfile={onViewProfile}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default GroupDetail;
